import React, { Component } from 'react';

import { Helmet } from 'react-helmet';

import { Box, Container, Stack } from '@mui/material';

import PageHeader from '~/components/Common/Elements/PageHeader';
import ErrorDisplay from '~/components/Common/ErrorDisplay';
import Spinner from '~/components/Common/Spinner';

import LedOffersService from '~/services/LedOffersService';
import OffersService from '~/services/OffersService';
import { getAddonPerformance, getOfferPerformanceForFinance } from '~/services/ReportingService';
import VendorsService from '~/services/VendorsService';

import { reportError } from '~/utils/reportError';

import ErrorPaymentsPane from './ErrorPayments/ErrorPaymentsPane';
import ManualPaymentsPane from './ManualPayments/ManualPaymentsPane';
import OverviewPane from './OverviewPane';
import VendorBookingPaymentsPane from './VendorBookingPayments/VendorBookingPaymentsPane';
import VendorOfferPaymentsPane from './VendorOfferPayments/VendorOfferPaymentsPane';
import VendorReport from './VendorReport';

export default class FinanceOfferDetailContainer extends Component {
  state = {
    offer_result: null,
    isLedExperience: false,
    vendor_offer_payments: null,
    offer_performance: null,
    error_payments: null,
    total_error_payments: {
      amount: 0,
      currency: 'AUD',
    },
    manual_payments: null,
    total_manual_payments: {
      amount: 0,
      currency: 'AUD',
    },
    is_loading: true,
    error: null,
  };

  componentDidMount() {
    this.fetchData();
  }

  fetchData = async () => {
    const offer_id = this.props.match.params.id_offer;

    this.setState({ is_loading: true, error: null });

    try {
      // Register LED offer query with index 1
      const offerData = await Promise.allSettled([
        OffersService.getOffer(offer_id, { filter: 'finance' }),
        LedOffersService.getOffer(offer_id),
      ]);

      const fulfilledPromiseResponse = offerData.find((d) => d.status === 'fulfilled');
      const offer_result = fulfilledPromiseResponse?.value;

      if (!offer_result) {
        throw new Error(`Offer ${offer_id} not found.`);
      }
      // Settled promise resolves in order of the promises registered
      // so an index of 1 here refers to LED experience/offer
      // Sadly, LED offers do not have a type set on them which would have
      // been the right way to figure this.
      const isLedExperience = offerData[1].status === 'fulfilled';
      const isAddon = offer_result.result.type === 'add-on';

      const getPerformance = isAddon ? getAddonPerformance : getOfferPerformanceForFinance;

      const [vendorPayments, offerPerformance, errorPayments, manualPayments] = await Promise.all([
        VendorsService.getPaymentsByOfferVendor(offer_id, offer_result.result.vendor_account_id),
        getPerformance(offer_id),
        VendorsService.getAllErrorPayments({ offerId: offer_id }),
        VendorsService.getAllManualPayments({ offerId: offer_id }),
      ]);

      this.setState({
        is_loading: false,
        isLedExperience,
        offer_result: offer_result,
        vendor_offer_payments: vendorPayments,
        offer_performance: isAddon ? offerPerformance.performance : offerPerformance.result,
        error_payments: errorPayments.result,
        total_error_payments: this.totalPayments(errorPayments.result),
        manual_payments: manualPayments.result,
        total_manual_payments: this.totalPayments(manualPayments.result),
      });
    } catch (error) {
      this.setState({ is_loading: false, error: error });
    }
  };

  totalPayments = (payments) => {
    const paymentsArray = payments ?? [];
    return {
      amount: paymentsArray.reduce((acc, payment) => acc + parseFloat(payment.amount), 0),
      currency: paymentsArray[0]?.currency ?? 'AUD',
    };
  };

  handleErrorPaymentAdded = (errorPayment) => {
    const error_payments = [errorPayment, ...this.state.error_payments].sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
    );
    this.setState({
      error_payments,
      total_error_payments: this.totalPayments(error_payments),
    });
  };

  handleErrorPaymentUpdated = (errorPayment) => {
    this.setState((prevState) => {
      const updatedErrorPayments = prevState.error_payments.map((payment) =>
        payment.id_adjustment === errorPayment.id_adjustment ? errorPayment : payment,
      );

      return {
        error_payments: updatedErrorPayments,
        total_error_payments: this.totalPayments(updatedErrorPayments),
      };
    });
  };

  handleErrorPaymentDeleted = (id) => {
    const error_payments = this.state.error_payments.filter((payment) => payment.id_adjustment !== id);
    this.setState({
      error_payments,
      total_error_payments: this.totalPayments(error_payments),
    });
  };

  handleManualPaymentAdded = (manualPayment) => {
    const manual_payments = [manualPayment, ...this.state.manual_payments].sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
    );
    this.setState({
      manual_payments,
      total_manual_payments: this.totalPayments(manual_payments),
    });
  };

  handleManualPaymentUpdated = (manualPayment) => {
    this.setState((prevState) => {
      const updatedManualPayments = prevState.manual_payments.map((payment) =>
        payment.id_manual_payment === manualPayment.id_manual_payment ? manualPayment : payment,
      );

      return {
        manual_payments: updatedManualPayments,
        total_manual_payments: this.totalPayments(updatedManualPayments),
      };
    });
  };

  handleManualPaymentDeleted = (id) => {
    const manual_payments = this.state.manual_payments.filter((payment) => payment.id_manual_payment !== id);
    this.setState({
      manual_payments,
      total_manual_payments: this.totalPayments(manual_payments),
    });
  };

  render() {
    const { match } = this.props;
    const {
      is_loading,
      error,
      offer_result,
      vendor_offer_payments,
      offer_performance,
      error_payments,
      total_error_payments,
      manual_payments,
      total_manual_payments,
      isLedExperience,
    } = this.state;

    if (is_loading) {
      return <Spinner />;
    }

    if (error) {
      return <ErrorDisplay message={error.message} report={reportError(error)} />;
    }

    return (
      <>
        <Helmet>
          <title>Finance | {offer_result?.result?.name || match.params.id_offer}</title>
        </Helmet>
        <Container>
          <PageHeader title={`Finance | ${offer_result?.result?.name || match.params.id_offer}`} />

          <Box mt={4}>
            <Stack direction="column" spacing={4}>
              <OverviewPane
                offer={offer_result}
                offerPayments={vendor_offer_payments}
                offerPerformance={offer_performance}
                totalErrorPayments={total_error_payments}
                totalManualPayments={total_manual_payments}
                isLedExperience={isLedExperience}
              />
              <ErrorPaymentsPane
                offerId={match.params.id_offer}
                vendorId={offer_result.result.vendor_account_id}
                errorPayments={error_payments}
                totalErrorPayments={total_error_payments}
                onErrorPaymentAdded={this.handleErrorPaymentAdded}
                onErrorPaymentUpdated={this.handleErrorPaymentUpdated}
                onErrorPaymentDeleted={this.handleErrorPaymentDeleted}
              />
              <ManualPaymentsPane
                offerId={match.params.id_offer}
                vendorId={offer_result.result.vendor_account_id}
                manualPayments={manual_payments}
                totalManualPayments={total_manual_payments}
                onManualPaymentAdded={this.handleManualPaymentAdded}
                onManualPaymentUpdated={this.handleManualPaymentUpdated}
                onManualPaymentDeleted={this.handleManualPaymentDeleted}
              />
              <div className="T-Offer-Payments">
                <VendorOfferPaymentsPane
                  idOffer={match.params.id_offer}
                  idVendor={offer_result.result.vendor_account_id}
                  data={vendor_offer_payments}
                />
              </div>
              <div className="T-Booking-Payments">
                <VendorBookingPaymentsPane
                  id_offer={match.params.id_offer}
                  id_vendor={offer_result.result.vendor_account_id}
                />
              </div>
              <div>
                <VendorReport offerId={match.params.id_offer} />
              </div>
            </Stack>
          </Box>
        </Container>
      </>
    );
  }
}
